import { useEffect, useMemo, useRef, useState } from 'react';
import { PALETTE } from '../palette.js';
import { api } from '../api.js';
import { Button, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import { IconCheck, IconChevron, IconExternal, IconInfo, IconPlay, IconRefresh, IconSettings } from '../components/icons.jsx';
import { safeFailureMessage } from '../safe-failure.js';

const ACTIVE_STATUSES = new Set(['queued', 'running']);

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function activeCompanyId(session) {
  const memberships = session.user?.memberships || [];
  return session.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function statusTone(tone, status) {
  if (tone === 'success' || status === 'succeeded' || status === 'waiting_approval') return 'live';
  if (tone === 'warn' || status === 'failed' || status === 'canceled') return 'warn';
  if (tone === 'active' || status === 'running') return 'accent';
  return 'neutral';
}

function fmtDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtDuration(value) {
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms < 0) return '-';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function elapsedDuration(startedAt, tick) {
  if (!startedAt) return null;
  void tick;
  return Math.max(0, Date.now() - Date.parse(startedAt));
}

function labelize(value, fallback = 'Pending') {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function workflowHeadline(job) {
  if (job?.status === 'failed') return 'This campaign needs attention.';
  if (job?.status === 'canceled') return 'This campaign build was canceled.';
  if (['succeeded', 'waiting_approval'].includes(job?.status)) return 'Your campaign is ready to review.';
  if (job?.context?.revision) return 'We are revising your campaign.';
  return 'We are building your campaign.';
}

function workflowMessage(job) {
  if (job?.failure) return job.failure.message;
  if (['succeeded', 'waiting_approval'].includes(job?.status)) {
    return 'The campaign assets are assembled. Review the brief, funnel, scripts, videos, and publish readiness next.';
  }
  return job?.progress?.message || 'This build continues safely if you leave the page.';
}

function Ring({ pct, size = 184, stroke = 7 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ display: 'block', maxWidth: '100%' }} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={PALETTE.lineSoft} strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={PALETTE.accent}
        strokeWidth={stroke}
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset .25s ease' }}
      />
    </svg>
  );
}

export default function WorkflowProgress({ navigate, params, session }) {
  const workflowJobId = params[0] || '';
  const companyId = activeCompanyId(session);
  const isSaasSession = session.user?.auth?.type === 'supabase';
  const activeContextKey = `${companyId}:${workflowJobId}`;
  const requestVersion = useRef(0);
  const activeContextRef = useRef(activeContextKey);
  const jobRef = useRef(null);
  const actionStatusRef = useRef('idle');
  const operationSequenceRef = useRef(0);
  const confirmationDialog = useConfirmationDialog();
  const [storedJob, setStoredJob] = useState(null);
  const [storedContextKey, setStoredContextKey] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [action, setAction] = useState({ status: 'idle', message: '' });
  const [tick, setTick] = useState(0);
  const workflowContextCurrent = storedContextKey === activeContextKey;
  const job = workflowContextCurrent ? storedJob : null;
  activeContextRef.current = activeContextKey;
  jobRef.current = job;
  actionStatusRef.current = action.status;

  async function loadWorkflow({ initial = false } = {}) {
    if (!isSaasSession || !companyId || !workflowJobId) return;
    const version = ++requestVersion.current;
    if (initial) {
      setStoredJob(null);
      setStoredContextKey('');
      setStatus('loading');
    }
    setError(null);
    try {
      const response = await api.get(`/api/saas/workflows/${workflowJobId}`, tenantHeaders(companyId));
      if (version !== requestVersion.current) return;
      setStoredJob(response.workflowJob || null);
      setStoredContextKey(activeContextKey);
      setStatus('ready');
    } catch (err) {
      if (version !== requestVersion.current) return;
      setError(err);
      if (initial || !job) {
        setStoredJob(null);
        setStoredContextKey('');
        setStatus('error');
      } else {
        setStatus('ready');
      }
    }
  }

  useEffect(() => {
    requestVersion.current += 1;
    operationSequenceRef.current += 1;
    actionStatusRef.current = 'idle';
    setStoredJob(null);
    setStoredContextKey('');
    setError(null);
    setStatus('idle');
    setAction({ status: 'idle', message: '' });
    loadWorkflow({ initial: true });
    return () => { requestVersion.current += 1; };
  }, [isSaasSession, companyId, workflowJobId]);

  useEffect(() => {
    if (!job || !ACTIVE_STATUSES.has(job.status)) return undefined;
    const id = setInterval(() => loadWorkflow(), 3000);
    return () => clearInterval(id);
  }, [job?.id, job?.status, companyId]);

  useEffect(() => {
    if (!job?.steps?.some((step) => step.status === 'running')) return undefined;
    const id = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(id);
  }, [job?.id, job?.steps]);

  async function runOperation(name, path, body) {
    if (actionStatusRef.current === 'working') return;
    const operationId = ++operationSequenceRef.current;
    const operationContext = activeContextKey;
    const operationTargetId = jobRef.current?.id || workflowJobId;
    actionStatusRef.current = 'working';
    setAction({ status: 'working', message: `${name} requested...` });
    try {
      await api.post(path, body, tenantHeaders(companyId));
      if (
        operationSequenceRef.current !== operationId
        || activeContextRef.current !== operationContext
        || jobRef.current?.id !== operationTargetId
      ) return;
      await loadWorkflow();
      if (operationSequenceRef.current !== operationId || activeContextRef.current !== operationContext) return;
      setAction({ status: 'done', message: `${name} request accepted.` });
    } catch (err) {
      if (operationSequenceRef.current === operationId && activeContextRef.current === operationContext) {
        setAction({ status: 'error', message: safeFailureMessage(err, 'workflow') });
      }
    } finally {
      if (operationSequenceRef.current === operationId) actionStatusRef.current = 'idle';
    }
  }

  async function confirmCancelBuild() {
    const requestContext = activeContextKey;
    const targetId = job?.id;
    if (!targetId || actionStatusRef.current === 'working' || !job?.actions?.canCancel) return;
    const approved = await confirmationDialog.request({
      eyebrow: 'Campaign build',
      title: 'Cancel this campaign build?',
      description: 'The active workflow will stop. Completed work remains available, but unfinished steps will not continue.',
      confirmLabel: 'Cancel build',
      cancelLabel: 'Keep building',
      tone: 'danger',
    });
    if (!approved) return;
    if (
      activeContextRef.current !== requestContext
      || jobRef.current?.id !== targetId
      || !jobRef.current?.actions?.canCancel
      || actionStatusRef.current === 'working'
    ) return;
    await runOperation(
      'Cancel',
      `/api/saas/workflows/${targetId}/cancel`,
      { reason: 'customer_cancel_from_progress' },
    );
  }

  const progress = Math.max(0, Math.min(100, Number(job?.progress?.percent ?? 0)));
  const pct = progress / 100;
  const context = job?.context || {};
  const campaign = context.campaign || {};
  const brand = context.brand || {};
  const steps = Array.isArray(job?.steps) ? job.steps : [];
  const summary = useMemo(() => job ? [
    { label: 'Status', value: job.progress?.statusLabel || labelize(job.status) },
    { label: 'Current step', value: job.progress?.label || labelize(job.currentStep) },
    { label: 'Attempt', value: job.attempt?.attemptNumber ? `#${job.attempt.attemptNumber}` : 'Preparing' },
    { label: 'Started', value: fmtDateTime(job.attempt?.startedAt || job.startedAt || job.createdAt) },
  ] : [], [job]);
  const showInitialWorkflowLoading = Boolean(companyId)
    && Boolean(workflowJobId)
    && !job
    && (!workflowContextCurrent || ['idle', 'loading'].includes(status));

  if (!isSaasSession) {
    return <Page><TopBar navigate={navigate} /><EmptyState title="SaaS session unavailable" body="Sign in with a SaaS account to view workflow progress." /></Page>;
  }

  return (
    <Page>
      <TopBar navigate={navigate} companyName={session.user?.activeCompany?.name} />
      {showInitialWorkflowLoading ? (
        <ProtectedPageLoader />
      ) : status === 'error' && !job ? (
        <EmptyState title="Workflow unavailable" body={safeFailureMessage(error, 'workflow')} tone="warn" />
      ) : job ? (
        <main className="workflow-progress-main">
          <section className="workflow-progress-hero">
            <div style={{ minWidth: 0 }}>
              <div className="workflow-kicker">
                {[brand.name, campaign.projectType, campaign.offer].filter(Boolean).join(' · ') || 'Campaign build'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <Pill tone={statusTone(job.progress?.tone, job.status)}>{job.progress?.statusLabel || labelize(job.status)}</Pill>
                {campaign.name && <span style={{ color: PALETTE.ink3, fontSize: 12 }}>{campaign.name}</span>}
              </div>
              <h1 className="workflow-progress-title">{workflowHeadline(job)}</h1>
              <p style={bodyCopyStyle}>{workflowMessage(job)}</p>
              {context.revision && (
                <div className="workflow-revision-context">
                  <strong>Requested changes</strong>
                  <span>{context.revision.feedback}</span>
                  <div>{context.revision.affectedAreas?.map((area) => <Pill key={area} tone="accent">{labelize(area)}</Pill>)}</div>
                </div>
              )}
              <div className="workflow-actions">
                <Button variant="accent" size="md" onClick={() => loadWorkflow()} disabled={action.status === 'working'}>
                  <IconRefresh size={14} /> Refresh
                </Button>
                {job.actions?.canRetry && (
                  <Button variant="accent" size="md" disabled={action.status === 'working'} onClick={() => runOperation(
                    'Retry',
                    `/api/saas/workflows/${workflowJobId}/retry`,
                    { retryFromStep: job.failure?.adminDetail?.step || job.progress?.step, reason: 'customer_retry_from_progress' },
                  )}>Retry from where it stopped</Button>
                )}
                {job.actions?.canCancel && (
                  <Button variant="ghost" size="md" disabled={action.status === 'working'} onClick={confirmCancelBuild}>Cancel build</Button>
                )}
                {job.actions?.canRecover && (
                  <Button variant="ghost" size="md" onClick={() => navigate('recovery')}><IconSettings size={14} /> Recovery</Button>
                )}
                {job.actions?.canReview && (
                  <Button variant="accent" size="md" onClick={() => navigate(`campaign-review/${job.id}`)}>Review campaign <IconChevron size={14} /></Button>
                )}
              </div>
              {action.message && <div className={`workflow-action-message ${action.status}`}>{action.message}</div>}
              {error && job && (
                <div className="workflow-refresh-warning" role="status">
                  Latest refresh failed safely. Showing the last confirmed campaign progress.
                </div>
              )}
            </div>
            <div style={ringWrapStyle} aria-label={`${progress}% complete`}>
              <Ring pct={pct} />
              <div style={ringCenterStyle}>
                <div>
                  <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 58, lineHeight: 1, color: PALETTE.ink }}>
                    {progress}<span style={{ fontSize: 22, color: PALETTE.ink3 }}>%</span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: PALETTE.ink3, textTransform: 'uppercase' }}>
                    {['succeeded', 'waiting_approval'].includes(job.status)
                      ? 'Complete'
                      : job.status === 'failed' ? 'Attention' : 'Progress'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="workflow-progress-grid">
            <div style={panelStyle}>
              <SectionTitle icon={<IconPlay size={16} />} title="Workflow Steps" />
              {steps.length ? (
                <div style={{ display: 'grid', gap: 9 }}>
                  {steps.map((step) => <StepRow key={step.key} step={step} tick={tick} />)}
                </div>
              ) : (
                <EmptyInline>The workflow engine is preparing the first persisted step.</EmptyInline>
              )}
            </div>

            <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
              <div className="workflow-deliverables">
                <div className="workflow-panel-label">What we are building</div>
                <div className="workflow-deliverable-grid">
                  {(job.deliverables || []).map((item) => (
                    <div key={item.key}>
                      <div className="workflow-deliverable-value">{item.count ?? labelize(item.status)}</div>
                      <div className="workflow-deliverable-label">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={panelStyle}>
                <SectionTitle icon={<IconInfo size={16} />} title="Campaign Detail" />
                <div style={summaryGridStyle}>{summary.map((item) => <DetailItem key={item.label} {...item} />)}</div>
              </div>
              {job.links?.funnel && (
                <div style={panelStyle}>
                  <div className="workflow-panel-label">Funnel source ready</div>
                  <a href={job.links.funnel} target="_blank" rel="noreferrer" style={linkStyle}>Open generated funnel <IconExternal size={13} /></a>
                </div>
              )}
              {job.failure && (
                <div style={{ ...panelStyle, background: PALETTE.warnSoft }}>
                  <SectionTitle icon={<IconInfo size={16} />} title="What needs attention" />
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: PALETTE.warn }}>{job.failure.message}</p>
                  {job.failure.adminDetail && (
                    <div style={adminDetailStyle}>
                      <DetailItem label="Code" value={job.failure.adminDetail.code} />
                      <DetailItem label="Step" value={labelize(job.failure.adminDetail.step, '-')} />
                      <DetailItem label="Retryable" value={job.failure.adminDetail.retryable ? 'Yes' : 'No'} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </main>
      ) : <EmptyState title="Workflow unavailable" body="Workflow progress is unavailable." tone="warn" />}
      <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </Page>
  );
}

function Page({ children }) {
  return <div style={{ minHeight: '100vh', background: PALETTE.bg, color: PALETTE.ink, fontFamily: 'Inter, sans-serif' }}>{children}</div>;
}

function TopBar({ navigate, companyName }) {
  return (
    <header style={topBarStyle}>
      <button onClick={() => navigate('campaigns')} style={backButtonStyle}>
        <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><IconChevron size={13} /></span>Campaigns
      </button>
      <div style={{ fontSize: 12, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{companyName || 'Active company'}</div>
    </header>
  );
}

function SectionTitle({ icon, title }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: PALETTE.ink, fontSize: 14, fontWeight: 700, marginBottom: 16 }}><span style={{ color: PALETTE.accent }}>{icon}</span>{title}</div>;
}

function StepRow({ step, tick }) {
  const done = ['done', 'succeeded', 'skipped'].includes(step.status);
  const failed = ['failed', 'canceled'].includes(step.status);
  const active = step.active || step.status === 'running';
  const duration = active ? elapsedDuration(step.startedAt, tick) : step.durationMs;
  const progressCurrent = step.progress?.current ?? step.children?.filter((child) => ['done', 'succeeded', 'skipped'].includes(child.status)).length;
  const progressTotal = step.progress?.total ?? step.children?.length;
  return (
    <div className={`workflow-step ${active ? 'active' : ''}`}>
      <div className={`workflow-step-state ${failed ? 'failed' : done ? 'done' : ''}`}>
        {done ? <IconCheck size={13} /> : failed ? '!' : ''}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="workflow-step-heading">{step.label}</div>
        <div className="workflow-step-detail">{step.detail}</div>
        {active && progressTotal > 0 && <div className="workflow-step-progress">{progressCurrent}/{progressTotal} complete</div>}
        {step.children?.length > 0 && (
          <div className="workflow-child-list">
            {step.children.map((child) => (
              <div key={child.key} className="workflow-child-row">
                <span className={`workflow-child-dot ${child.status}`} />
                <span>{child.label}</span>
                <span>{child.durationMs !== null ? fmtDuration(child.durationMs) : labelize(child.status)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="workflow-step-duration">{duration !== null ? fmtDuration(duration) : labelize(step.status, '-')}</div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return <div style={{ minWidth: 0 }}><div style={{ fontSize: 11, color: PALETTE.ink3, marginBottom: 5 }}>{label}</div><div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 700, overflowWrap: 'anywhere' }}>{value}</div></div>;
}

function EmptyInline({ children }) {
  return <div style={{ padding: 16, background: PALETTE.surfaceAlt, borderRadius: 8, color: PALETTE.ink3, fontSize: 13 }}>{children}</div>;
}

function EmptyState({ title, body, tone = 'neutral' }) {
  return <div style={{ maxWidth: 680, margin: '80px auto', padding: 28, border: `1px solid ${PALETTE.line}`, borderRadius: 10, background: tone === 'warn' ? PALETTE.warnSoft : PALETTE.surface }}><div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{title}</div><div style={{ fontSize: 14, color: tone === 'warn' ? PALETTE.warn : PALETTE.ink2, lineHeight: 1.55 }}>{body}</div></div>;
}

const topBarStyle = { height: 56, padding: '0 clamp(16px, 4vw, 32px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, borderBottom: `1px solid ${PALETTE.lineSoft}`, background: PALETTE.bg };
const backButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: PALETTE.ink3, cursor: 'pointer', fontSize: 13, padding: 0 };
const bodyCopyStyle = { maxWidth: 620, margin: '18px 0 0', fontSize: 16, lineHeight: 1.55, color: PALETTE.ink2 };
const ringWrapStyle = { width: 184, height: 184, position: 'relative', justifySelf: 'center' };
const ringCenterStyle = { position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' };
const panelStyle = { padding: 20, border: `1px solid ${PALETTE.line}`, borderRadius: 10, background: PALETTE.surface };
const summaryGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 18 };
const adminDetailStyle = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${PALETTE.line}` };
const linkStyle = { display: 'inline-flex', alignItems: 'center', gap: 7, color: PALETTE.accentDeep, fontWeight: 700, fontSize: 13, overflowWrap: 'anywhere' };
